/**
 * The AS/400 claim-amount formula (RPG program OPE84440), shared by every
 * surface that shows or computes a Ship & Debit claim amount.
 *
 * WHY THIS IS SHARED
 * ------------------
 * The formula was implemented twice with two different number systems: the
 * backend in `Decimal` with an explicit ROUND_HALF_UP, and the S&D transaction
 * modal's live preview in floating point (`Math.round(x * 100) / 100`). Those
 * disagree — `Math.round` operates on the binary float, so a product landing
 * exactly on a half-cent rounds the other way, and large quantities accumulate
 * representation error the Decimal path does not have.
 *
 * The preview is display-only (the modal never sends `claimAmount`; the backend
 * recalculates it), so the divergence showed up as a number the user read
 * before saving that did not match the number that came back. One definition
 * removes that class of surprise.
 *
 * THE RULE
 * --------
 * RPG @RtvWin3 (page 39):
 *   Eval(H)  #3WAMT0 = #3QTY * #3WPRIC    (line 195261)
 *   Eval(H)  #3QAMT0 = #3QTY * #3QPRIC    (line 195461)
 *   Eval     #3AMT   = #3WAMT0 - #3QAMT0  (line 195561)
 *
 * `#3WAMT0` / `#3QAMT0` are S(16,2), and `Eval(H)` half-adjusts — so EACH
 * extension is rounded to 2 decimals BEFORE the subtraction. Rounding only the
 * final difference gives a different answer, and the intermediate rounding is
 * what matches real AS/400 output.
 */

import type { Money, MoneyInput } from "./money";
import { multiplyMoney, roundMoney, subtractMoney } from "./money";

export interface ClaimAmountInput {
  /** RDQTY — invoiced quantity. */
  quantity: MoneyInput;
  /** RDWPRIC — wholesale unit price. */
  wholesalePrice: MoneyInput;
  /** RDQPRIC — quoted unit price. */
  quotedPrice: MoneyInput;
}

export interface ClaimAmountBreakdown {
  /** #3WAMT0 — extended wholesale, rounded to 2dp. */
  extendedWholesale: Money;
  /** #3QAMT0 — extended quoted, rounded to 2dp. */
  extendedQuoted: Money;
  /** #3AMT — the claim amount. */
  claimAmount: Money;
}

/**
 * Compute the claim amount and both extensions.
 *
 * Use this when you need to show the intermediates (the AS/400 screen displays
 * them); otherwise use `computeClaimAmount`.
 */
export function computeClaimAmountBreakdown(
  input: ClaimAmountInput,
): ClaimAmountBreakdown {
  // Eval(H) rounds each intermediate to 2 decimal places.
  const extendedWholesale = roundMoney(
    multiplyMoney(input.quantity, input.wholesalePrice),
    2,
    "ROUND_HALF_UP",
  );
  const extendedQuoted = roundMoney(
    multiplyMoney(input.quantity, input.quotedPrice),
    2,
    "ROUND_HALF_UP",
  );
  // Both are already at 2dp — the difference takes no further rounding.
  return {
    extendedWholesale,
    extendedQuoted,
    claimAmount: subtractMoney(extendedWholesale, extendedQuoted),
  };
}

/** Compute the claim amount (#3AMT) alone. */
export function computeClaimAmount(input: ClaimAmountInput): Money {
  return computeClaimAmountBreakdown(input).claimAmount;
}
